import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Mic, Square, Send, Edit3 } from "lucide-react";
import api from "@/lib/api";
import Mashy from "@/components/Mashy";
import { VOICE_PROMPTS } from "@/lib/mockData";

const SAMPLE_PROMPTS = VOICE_PROMPTS;

export default function VoicePractice() {
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [language, setLanguage] = useState(null);
  const [aiReply, setAiReply] = useState(null);
  const [prompt, setPrompt] = useState(SAMPLE_PROMPTS[0]);
  const recogRef = useRef(null);

  const start = () => {
    setAiReply(null);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const r = new SR();
      r.lang = "en-IN";
      r.interimResults = false;
      r.continuous = false;
      r.onresult = (e) => setTranscript(e.results[0][0].transcript);
      r.onend = () => setRecording(false);
      r.onerror = () => setRecording(false);
      recogRef.current = r;
      r.start();
      setRecording(true);
    } else {
      // No speech API — simulate
      setRecording(true);
      setTimeout(() => {
        setTranscript(prompt.en);
        setRecording(false);
      }, 1200);
    }
  };

  const stop = () => {
    recogRef.current?.stop?.();
    setRecording(false);
  };

  const submit = async () => {
    try {
      const r = await api.post("/voice/transcribe", { text: transcript, language: "auto" });
      setLanguage(r.data.detected_language);
      const t = await api.post("/tutor/english", {
        message: `Student said: "${transcript}". Expected: "${prompt.en}". Give feedback.`,
        subject: "english",
        language: "hinglish",
      });
      setAiReply(t.data.reply);
    } catch (e) {
      setAiReply("Great effort! Try once more.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-24" data-testid="voice-screen">
      <header className="px-5 pt-6 max-w-xl mx-auto">
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Voice Practice</div>
        <h1 className="font-display text-2xl">Bolo Mashy ke saath</h1>
      </header>

      <main className="max-w-xl mx-auto px-5 mt-6 flex flex-col items-center gap-6">
        <div className="card-chunky p-5 w-full text-center">
          <div className="text-xs uppercase font-bold text-gray-500 tracking-widest">Say this</div>
          <div className="font-display text-2xl mt-1">"{prompt.en}"</div>
          <div className="text-gray-500 mt-1">{prompt.hi}</div>
          <div className="flex gap-2 justify-center mt-3">
            {SAMPLE_PROMPTS.map((p, i) => (
              <button key={i} onClick={() => setPrompt(p)} data-testid={`voice-prompt-${i}`} className={`text-xs px-3 py-1 rounded-full font-bold ${prompt.en === p.en ? "bg-[#1CB0F6] text-white" : "bg-gray-100 text-gray-600"}`}>
                {i + 1}
              </button>
            ))}
          </div>
        </div>

        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={recording ? stop : start}
          data-testid="voice-mic-button"
          className={`w-40 h-40 rounded-full flex items-center justify-center text-white shadow-xl ${recording ? "bg-[#FF4B4B] mic-pulse" : "bg-[#1CB0F6]"}`}
        >
          {recording ? <Square className="w-16 h-16" /> : <Mic className="w-20 h-20" strokeWidth={2.5} />}
        </motion.button>
        <div className="text-sm font-bold text-gray-500">{recording ? "Listening… tap to stop" : "Tap to record"}</div>

        {transcript && (
          <div className="w-full card-chunky p-4" data-testid="voice-transcript-card">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Your Transcript</div>
              <Edit3 className="w-4 h-4 text-gray-400" />
            </div>
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              data-testid="voice-transcript-input"
              className="w-full border-2 border-gray-200 rounded-xl p-3 text-lg focus:border-[#1CB0F6] outline-none"
              rows={2}
            />
            {language && <div className="text-xs text-gray-500 mt-1">Detected: <b>{language}</b></div>}
            <button onClick={submit} data-testid="voice-submit" className="btn-primary mt-3 w-full">
              <Send className="w-4 h-4" /> Send to Mashy
            </button>
          </div>
        )}

        {aiReply && (
          <div className="w-full flex items-end gap-3" data-testid="voice-ai-reply">
            <Mashy mood="happy" size={70} />
            <div className="card-chunky px-4 py-3 flex-1 whitespace-pre-wrap">{aiReply}</div>
          </div>
        )}
      </main>
    </div>
  );
}
